import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'

import { PRODUCT_STOCK_STATUSES, PRODUCT_STATUSES } from 'constants/statuses'

import type { Product } from 'types'

// Extend Product type for modal compatibility
interface ProductWithCertificatesCount extends Product {
  certificates_count: number
}

import { useProduct, useProductDefaultTagMutations, useProductMutations, useSyncProductFromShopify, useProductSyncMode, useCertificateTriggerSettings, useCurrentUserCompany, useConvertDemoProduct } from 'hooks/api'
import { useIsDemoCompany } from 'hooks'
import { activationTriggerTitle } from 'constants/activationTriggers'
import { resolveAccessSetting, resolveActivationTrigger } from '@repo/types'

import type { AccessSettingKey } from 'constants/accessSettings'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'
import { PencilIcon, BadgePlus, TagIcon, RefreshCw, TrashIcon, ArchiveIcon, ArchiveRestoreIcon, ExternalLinkIcon, FlaskConicalOffIcon } from 'lucide-react'
import { ROUTES } from 'routes'

// Tooltip from MUI, not @repo/ui — that package's Tooltip is a 0-byte stub that
// resolves in TypeScript and exports nothing at runtime (working-conventions §47).
import { Stack, Typography, Grid, Box, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { Alert, Button, ProductModel } from '@repo/ui'
import { Header, InfoBlock, InfoRow, CategoryCell, ClampedRichText, InfoItem, Slideshow, ConvertDemoRowModal, CreateCertificatesModal, DeleteProductModal, SetDefaultTagModal, DefaultTagProductInfo, ActivationTriggerItem, EditActivationTriggerModal, AccessSettingItem, EditAccessSettingModal } from 'components'
import { StatusChip } from 'components/display/StatusChip'
import { ExampleKindBadge } from 'components/display/ExampleKindBadge'
import { ProductSyncButton } from './components/ProductSyncButton'
import { ProductPageSkeleton } from './ProductPageSkeleton'



export const ProductPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const { data: product, isLoading, refetch } = useProduct({ productId: id as string })
  const { open: notification } = useSnackbar()

  // All useState hooks must be called before any conditional logic
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openCreateCertificatesModal, setOpenCreateCertificatesModal] = useState(false)
  const [openSetDefaultTagModal, setOpenSetDefaultTagModal] = useState(false)
  // VRG-528 — the product-level activation-trigger override.
  const [openActivationModal, setOpenActivationModal] = useState(false)
  /**
   * VRG-591 — WHICH access flag is being edited, or null for "no modal".
   *
   * One piece of state rather than two booleans: the two flags share one modal
   * component and only one can be open at a time, so two booleans would make an
   * impossible state (both open) representable and would need a rule about which
   * wins. The key doubles as the modal's `setting` prop.
   */
  const [editingAccessSetting, setEditingAccessSetting] = useState<AccessSettingKey | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)

  // Default tag mutations
  const { removeDefaultTag, isLoading: isRemovingTag } = useProductDefaultTagMutations({
    onSuccess: () => {
      refetch()
    },
    onError: error => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  // Sync from Shopify
  const { syncProduct, isLoading: isSyncing } = useSyncProductFromShopify({
    onSuccess: () => {
      refetch()
    },
  })

  // Archive / unarchive (soft-retire). Always allowed, even when delete is
  // blocked by linked certificates.
  // VRG-528 — the company default, so the row can say WHERE the resolved value came
  // from rather than just what it is.
  const companyQuery = useCurrentUserCompany()
  const companyId = companyQuery.data?.id ?? ''
  const { settings: triggerSettings } = useCertificateTriggerSettings(companyId, {
    enabled: !!companyId,
  })
  // What this product falls back to with no override of its own — hoisted because
  // the picker needs it BOTH as a title and as an id (the id is what carries the
  // trigger's glyph onto the inherit row), and resolving it twice inline would be
  // two places to keep in step for one value.
  const inheritedTrigger = resolveActivationTrigger({
    company: triggerSettings?.activation_trigger ?? null,
  }).trigger

  const [{ setActivationTrigger }, { isSettingActivationTrigger }] =
    useProductMutations({
      onSuccess: () => {
        refetch()
        setOpenActivationModal(false)
      },
    })

  // VRG-591 — its own hook instance so its `onSuccess` closes the ACCESS modal
  // and not the activation one. Sharing the instance above would close whichever
  // modal the other mutation finished under.
  const [{ setAccessSetting }, { isSettingAccessSetting }] = useProductMutations({
    onSuccess: () => {
      refetch()
      setEditingAccessSetting(null)
    },
    onError: error => notification({ type: 'error', title: getErrorMessage(error) }),
  })

  const [{ archive }] = useProductMutations({
    onSuccess: () => {
      refetch()
    },
    onError: error => notification({ type: 'error', title: getErrorMessage(error) }),
  })
  const [{ unarchive }] = useProductMutations({
    onSuccess: () => {
      refetch()
    },
    onError: error => notification({ type: 'error', title: getErrorMessage(error) }),
  })

  // VRG-102 — per-product provider→Vera re-sync override.
  const { mutate: setSyncMode, isPending: isSettingSyncMode } = useProductSyncMode()

  const handleCreateCertificates = (row: Product) => {
    setOpenCreateCertificatesModal(true)
    setSelectedProduct(row)
  }

  const handleCloseCreateCertificatesModal = () => {
    setOpenCreateCertificatesModal(false)
    setSelectedProduct(null)
  }

  const handleRemoveDefaultTag = () => {
    if (!product?.id) return
    removeDefaultTag({
      product_id: product.id
    })
  }

  // Show loading state while fetching. The skeleton mirrors the loaded page's footprint (§19) —
  // see ProductPageSkeleton for the measurements and for what it deliberately does not reserve.
  if (isLoading) {
    return <ProductPageSkeleton />
  }

  // VRG-103 — a product with any linked certificate can never be deleted (only
  // archived); the API enforces this (RemoveProductUseCase, certificates.product_id
  // ON DELETE RESTRICT). Delete is hidden (not disabled) here per the record-state
  // rule — Archive is always present and reachable in this state (working-conventions
  // §46), so hiding never leaves the user without a way to retire the product.
  const hasCertificates = Boolean(product && (product.certificates_count ?? 0) > 0)

  /*
   * VRG-836 — "Convert to real" (`POST /demo-data/products/:id/convert`). Mirrors the
   * products LIST kebab item exactly, in the same slot, because a row and its own detail
   * page offering different actions for one record is the drift the kebab/detail parity
   * rule exists to prevent.
   *
   * FREE — unlike the certificate convert, this bills nothing. GATED BEFORE APPLYING
   * (§43): the button only opens the confirm.
   */
  const [openConvertDemoModal, setOpenConvertDemoModal] = useState(false)
  const isDemoCompany = useIsDemoCompany()
  const {
    convertDemoProductAsync,
    isConvertingDemoProduct,
    convertDemoProductError,
  } = useConvertDemoProduct()

  const handleConfirmConvertDemoProduct = async () => {
    if (!product) return
    try {
      const result = await convertDemoProductAsync(product.id)
      if (!result.converted) {
        // One refusal reason covers absent, another company's, already-real and `intro`
        // alike, by design, so this must NOT be rendered as "not found".
        notification({
          type: 'error',
          title:
            'This product is already real, or is no longer demo data. ' +
            'Reload to see its current state.',
        })
        return
      }
      setOpenConvertDemoModal(false)
      // Visible-action success — the Demo badge leaves the title — so no success toast.
    } catch {
      // Left open deliberately: closing on failure would read as success.
    }
  }

  // Handle case where product is not found
  if (!product) {
    return (
      <>
        <Header 
          title="Product not found"
          breadcrumbs={true}
        />
        <Alert severity="error" title="Product not found" description="The requested product could not be found." />
      </>
    )
  }

  return (
    <>
        <Header 
          breadcrumbsOveride={product.title}
          title={product.title}
          // VRG-361 — beside the title, not in `status`: the stock pill there is the
          // product's lifecycle state, and this is a fixed classification of the row.
          titleAdornment={<ExampleKindBadge kind={product.example_kind} />}
          status={{
            statuses: PRODUCT_STOCK_STATUSES,
            value: product.available ? 'in_stock' : 'not_in_stock',
          }}
          leftComponents={product.archived ? (
            // Default (medium) size to match the header's "In stock" StatusChip.
            <StatusChip statuses={PRODUCT_STATUSES} status="archived" />
          ) : undefined}
          breadcrumbs={true}
          rightComponents={
            <Stack direction="row" spacing={3} alignItems="center">
              {/* VRG-102 — split Sync button: main = Sync now, caret = Auto-sync policy. */}
              {product.integration_provider === 'shopify' && product.integration_id && (
                <ProductSyncButton
                  mode={product.sync_mode ?? 'inherit'}
                  syncing={isSyncing || isSettingSyncMode}
                  lastUpdatedAt={product.updated_at}
                  onSyncNow={() => syncProduct(product.id)}
                  onChangeMode={(m) =>
                    setSyncMode({ productId: product.id, syncMode: m }, { onSuccess: () => refetch() })
                  }
                />
              )}
              <Button
                variant="secondary"
                disableElevation
                onClick={() => navigate(ROUTES.PRODUCT_EDIT(id as string))}
                startIcon={<PencilIcon strokeWidth={1}/>}>
                Edit
              </Button>
              {/*
                VRG-836 — "Convert to real". A `secondary` button, NOT the primary: the page's
                primary stays "Create Certificates" (§26 — one primary per header, rightmost).
                Not `destructive` either (§32): converting ends a classification, it destroys
                nothing.

                Placed between Edit and Archive so Archive keeps its slot as the last
                non-destructive action (§73), matching this product's own row kebab item for
                item. It is record-state gated, so for every real product the header is
                unchanged — only a demo row shows the extra control, which is why this does not
                crowd the normal case.

                HIDDEN rather than disabled when unavailable (§46, record state): a real
                product has nothing to convert, and inside a demo COMPANY every row is Vera's
                and `ExampleKindBadge` is suppressed for the same reason — so the affordance
                appears exactly where the badge does.
              */}
              {product.example_kind === 'demo' && !isDemoCompany && (
                <Button
                  variant="secondary"
                  disableElevation
                  onClick={() => setOpenConvertDemoModal(true)}
                  startIcon={<FlaskConicalOffIcon strokeWidth={1} />}>
                  Convert to real
                </Button>
              )}
              {/* Archive sits between Edit and Delete (convention, §26). */}
              {product.archived ? (
                <Button
                  variant="secondary"
                  disableElevation
                  onClick={() => unarchive({ id: product.id })}
                  startIcon={<ArchiveRestoreIcon strokeWidth={1}/>}>
                  Unarchive
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  disableElevation
                  onClick={() => archive({ id: product.id })}
                  startIcon={<ArchiveIcon strokeWidth={1}/>}>
                  Archive
                </Button>
              )}
              {/* VRG-103 — hidden (not disabled) while the product has certificates;
                  Archive above is always reachable, so hiding never strands the user. */}
              {!hasCertificates && (
                <Button
                  variant="secondary"
                  destructive="hover"
                  disableElevation
                  onClick={() => setOpenDeleteModal(true)}
                  startIcon={<TrashIcon strokeWidth={1}/>}>
                  Delete
                </Button>
              )}
              <Button
                disableElevation
                onClick={() => handleCreateCertificates(product)}
                startIcon={<BadgePlus  strokeWidth={1}/>}>
                Create Certificates
              </Button>
            </Stack>
        }/>
        <>
          <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={5}>
                {/*
                  VRG-749 — the MODEL wins the hero slot when there is one.

                  Three branches, in this order, because they are three different facts
                  about the record and the order encodes which is most informative: a
                  model of THIS product beats a photograph of one, and a photograph beats
                  nothing. A model does not remove the photos — they stay on the edit
                  form and in the `images` column, and this page simply leads with the
                  better thing.

                  The square wrapper is what gives §19 footprint parity: ProductPageSkeleton
                  draws an aspectRatio 1/1 box in this exact grid slot (see its own note on
                  why the ratio must live on a wrapper), so the skeleton and both loaded
                  states occupy the same space and nothing shifts. `height="100%"` rather
                  than a number for the same reason — the wrapper owns the geometry.
                */}
                {product.model_url ? (
                  <ProductModel
                    src={product.model_url}
                    title={product.title}
                    // The RATIO, not a height: it matches the Slideshow and the
                    // skeleton without a wrapper, and a percentage height would
                    // collapse here (see the prop's own note).
                    aspectRatio="1 / 1"
                    // `panel` here, unlike the certificate hero: this page is a dense
                    // admin surface of bordered cards and info blocks, so an unframed
                    // canvas in the middle of it reads as a rendering glitch rather
                    // than as a deliberate full-bleed hero.
                    surface="panel"
                  />
                ) : product.images && product.images.length > 0 ? (
                  <Slideshow
                    images={product.images}
                    /*
                     * The same constant plate the model renders on, so the two hero
                     * branches of this page do not sit on different backgrounds. It
                     * shows only through artwork with transparency — the slide is
                     * `cover`, so an opaque photograph hides it entirely.
                     */
                    mediaPlate
                  />
                ) : (
                  <Box 
                    sx={{ 
                      aspectRatio: '1/1',
                      border: theme => `1px solid ${theme.palette.primary[300]}`,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme => theme.palette.primary[50]
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No image available
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  {/*
                    Titled, now that the two cards below it are. Headless was right
                    while NOTHING on the page carried a heading — the page title is
                    this card's heading, and that is what the platform's other 17
                    InfoBlocks rely on. The moment a sibling card gained one, the
                    absence stopped reading as "this is the primary block" and
                    started reading as "this one is missing its title".

                    Within-page consistency beats the cross-page pattern here: the
                    reader sees all four cards in one glance and sees none of the
                    other detail pages at the same time.

                    "Product details", not "Details" — `products.details` is a real
                    column (varchar 2000, distinct from `description`), and a card
                    named for a field it does not show is a trap for whoever adds it.
                  */}
                  <InfoBlock title="Product details">
                    <InfoRow>
                      <InfoItem label="SKU" value={product.sku || '-'} tooltip={false} />
                      <InfoItem label="Product ID" value={product.id || '-'} tooltip={false} />
                    </InfoRow>
                    <InfoRow>
                      <InfoItem
                        label="Product URL"
                        // `minWidth: 0` ONLY — no flex override. InfoItem already
                        // sets `width: 100%`, so two of them in a `space-between`
                        // row split evenly, which is what aligns SKU/Product ID and
                        // Type/Manufactured Date. This row needs the same split; it
                        // just also needs permission to shrink, because a flex child
                        // will not go below its content width without `minWidth: 0`
                        // and the URL is one long unbroken token.
                        //
                        // An earlier `flex: '0 1 50%'` here looked like the obvious
                        // fix and was worse: with the sibling sizing from content,
                        // the URL column collapsed to about a fifth of the row and
                        // the two rows stopped lining up at all.
                        sx={{ minWidth: 0 }}
                        value={product.product_url ? (
                          // ONE line, ellipsised, with the full URL in a tooltip.
                          // Product URLs carry variant ids and tracking params and
                          // routinely run past 100 characters; `wordBreak:
                          // 'break-all'` wrapped them mid-word over three lines,
                          // which pushed every field below out of alignment and
                          // still could not be read as a URL. The tail is the least
                          // informative part, so clipping it loses least, and the
                          // link itself remains the way to actually open it.
                          <Tooltip title={product.product_url} placement="top-start">
                            <Stack
                              component="a"
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              // External-link convention: plain text colour (not the
                              // brand/purple link colour) + a trailing external-link
                              // icon, matching the "Preview certificate" action.
                              href={product.product_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: 'text.primary',
                                textDecoration: 'none',
                                maxWidth: '100%',
                                minWidth: 0,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  // minWidth:0 is required for a flex child to be
                                  // allowed to shrink below its content width —
                                  // without it the ellipsis never engages.
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {product.product_url}
                              </Box>
                              <ExternalLinkIcon size={14} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                            </Stack>
                          </Tooltip>
                        ) : '-'}
                        tooltip={false}
                      />
                      <InfoItem label="Quantity" value={String(product.quantity)} tooltip={false} />
                    </InfoRow>
                    <InfoRow>
                      <InfoItem label="Type" value={product.type ? <CategoryCell category={product.type} filter /> : '-'} tooltip={false} />
                      <InfoItem 
                        label="Manufactured Date" 
                        value={product.manufactured_at ? format(new Date(product.manufactured_at), 'MMM dd, yyyy') : '-'} 
                        tooltip={false} 
                      />
                    </InfoRow>
                    {/*
                      Description is a FIELD of the product, so it lives here rather
                      than in a card of its own. Spencer: "description is a field
                      under product details, not its own card or group of info".
                      It is also what the mechanical rule says — it is on
                      ProductEditPage (SECTIONS "basic"), and everything on that page
                      belongs in this card.

                      It was an `<Accordion defaultExpanded>` below the cards, and
                      little was lost by deleting it: the collapsed state was never
                      persisted, so every page load rendered it open anyway, and the
                      panel carried ~40 lines of `sx` fighting MUI's
                      `:first-of-type` / `:last-of-type` border-radius defaults.

                      ⚠️ A ROW OF ITS OWN, deliberately NOT a second cell beside
                      "Manufactured Date". This value is rich HTML from the edit
                      page's RichTextEditorField, up to varchar(1000) — in a 2-up row
                      a long one makes its cell ten lines tall while the cell beside
                      it stays empty, which is the orphan the activation/access move
                      deleted a spacer to fix. A bare `<InfoRow>` is a flex row, so a
                      single child takes the full width.

                      `dangerouslySetInnerHTML` is carried over verbatim from the
                      accordion, not introduced here: the column has always stored
                      markup and this is the only thing that renders it.
                    */}
                    <InfoRow>
                      <InfoItem
                        label="Description"
                        tooltip={false}
                        value={
                          product.description ? (
                            <ClampedRichText html={product.description} />
                          ) : null
                        }
                      />
                    </InfoRow>
                  </InfoBlock>

                  {/*
                    ORDER: Default tag sits ABOVE Certificate defaults.

                    Spencer: "should default tag go above certificate defaults?" —
                    yes, on two counts.

                    Settings go LAST. "Certificate defaults" is configuration this
                    product hands DOWN to its certificates; "Default tag" is part of
                    what the product IS — which physical tag ships with it — so it
                    belongs next to Product details, and the inherited-settings block
                    reads as the tail of the stack rather than its middle.

                    And the empty state is a CALL TO ACTION. Beneath a settings panel
                    is the worst place for the one card on this page asking to be
                    filled, and it is also the card most likely to BE empty on a new
                    product — i.e. exactly when a reader needs to find it.
                  */}
                  {/*
                    A CARD like its siblings, titled, with no border of its own.

                    It was two hand-rolled `<Box>`es — a dashed-outlined prompt when
                    empty and a solid-bordered panel when filled — each carrying its
                    own surface, border and radius, and each labelling itself with an
                    in-card `body2`/500 "Default tag". Spencer: "lets make default tag
                    a card - no dashed border and give it the card title too".

                    Wrapping both states in ONE titled `InfoBlock` is what makes the
                    right-hand column read as three cards rather than two cards and a
                    pair of outliers, and it deletes the duplicate heading: the card
                    title now says "Default tag" in the same 18px/600 as "Product
                    details" and "Certificate defaults", in both states, so neither
                    branch has to label itself.

                    What is lost, named rather than quietly dropped: the dashed border
                    was the empty state's whole "this slot wants filling" signal
                    (§31's dashed-says-drop-something-here), and solid-vs-dashed read
                    as complete-vs-incomplete with no colour at all. The prompt
                    sentence plus the "Set Default Tag" button still say the slot is
                    empty, in words, which is why this is an acceptable trade rather
                    than a regression — but it IS a quieter empty state than before.

                    `px: 6, pb: 6` mirrors the title's own gutter, so the content lines
                    up with the heading above it and with the field columns in the
                    sibling cards. No `pt` — the title's `pb: 6` already supplies the
                    gap, and adding one here would double it.
                  */}
                  <InfoBlock title="Default tag">
                    <Box sx={{ px: 6, pb: 6, width: '100%' }}>
                      {product.tag_shop_product ? (
                        <DefaultTagProductInfo
                          tagProduct={product.tag_shop_product}
                          onRemoveDefault={handleRemoveDefaultTag}
                          onChangeDefault={() => setOpenSetDefaultTagModal(true)}
                        />
                      ) : (
                        /* Text left, action right in ONE row — parity with the filled
                           state (DefaultTagProductInfo: info left, Buy now right), so
                           the card doesn't restructure when a tag is selected. */
                        <Stack
                          direction="row"
                          spacing={4}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          {/*
                            `body3` (14px), NOT `body2` (16px). Spencer: "I feel like
                            this line is missing a field title like the others even
                            though it's not one?" — and the cause is measurable, not a
                            matter of taste. `InfoItem`'s field LABEL is 16px /
                            `text.secondary`, so at body2 this sentence had the exact
                            spec of a label: the eye parsed it as a label sitting
                            alone with its value missing.

                            14px / `text.secondary` is the house treatment for
                            descriptive text under a card heading — the same Settings
                            reference the 18px/600 heading itself was measured from
                            (FeaturesPage uses `body3` + `lineHeight: 1.6` for exactly
                            this). It moves the sentence out of the label tier into
                            the description tier, which is what it actually is.
                          */}
                          <Typography
                            variant="body3"
                            sx={{ color: 'text.secondary', lineHeight: 1.6, minWidth: 0 }}
                          >
                            Set a default tag for this product to enable direct ordering and advanced inventory alerts
                          </Typography>
                          <Button
                            disableElevation
                            variant="secondary"
                            startIcon={<TagIcon strokeWidth={1} />}
                            onClick={() => setOpenSetDefaultTagModal(true)}
                            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                          >
                            Set Default Tag
                          </Button>
                        </Stack>
                      )}
                    </Box>
                  </InfoBlock>

                  {/*
                    ITS OWN CARD, not three more rows in the Details block above.

                    The line is mechanical rather than a matter of taste: every other
                    row in Details has a counterpart on ProductEditPage, and these
                    three have NONE — grep `activation_trigger|collect_email|
                    collect_location` there and it returns nothing. They are not
                    attributes of the product record. They are this product's position
                    in a three-level inheritance chain, resolved by
                    `resolveActivationTrigger` / `resolveAccessSetting`:

                        certificate.x ?? product.x ?? company.x ?? system default

                    So Details is "what you typed"; this is "what this product hands
                    DOWN to its certificates, unless the certificate overrides it".
                    Hence "defaults" in the title: the word tells a reader that a
                    certificate may differ, which is the one thing a resolved value
                    with a provenance line cannot say on its own.

                    Two further reasons they could not stay above. Details answers to
                    `manage_product`; these answer to `billing_activation_settings`
                    and `certificate_access_settings`, so the card could not be gated
                    as a unit and a user holding only the first saw one row's Edit
                    disabled for a reason unrelated to the card. And everything in
                    Details is edited by LEAVING for the edit page, while these are
                    edited in place by modal — two edit models inside one box.

                    The spacer `<InfoItem label="" value={<></>} />` that used to end
                    the second row is gone with the move; it existed only to absorb the
                    orphaned grid slot, which is the layout admitting it did not fit.

                    `columns={2}` so the two access rows sit in the SAME tracks as
                    Activation above them rather than each row dividing itself evenly
                    — see InfoRow's own note on why a ragged panel does not scan.
                  */}
                  <InfoBlock title="Certificate defaults">
                    <InfoRow columns={2}>
                      {/*
                        VRG-528 — resolved value + WHERE it came from, editable via
                        modal. Gated inside the shared cell on
                        `billing_activation_settings`, the same permission the API
                        route carries.
                      */}
                      <ActivationTriggerItem
                        scope="product"
                        own={product.activation_trigger ?? null}
                        company={triggerSettings?.activation_trigger ?? null}
                        onEdit={() => setOpenActivationModal(true)}
                      />
                    </InfoRow>
                    {/*
                      VRG-591 — the two access requirements, side by side.

                      Side by side but STILL TWO FIELDS. Merging them into one control
                      with an icon per flag was considered and rejected, and the
                      resolver already settled the substance: the two walk INDEPENDENT
                      chains, so a product may pin `collect_location` while leaving
                      `collect_email` inheriting. One control implies one inherited
                      bundle, which is the model `access-settings.resolver.ts` rejects
                      at length — and each flag's provenance ("Company default" vs "Set
                      on this product") can differ, which a single row cannot carry.
                      They also take different confirm copy, since one is an access
                      GATE and the other is passive telemetry (§43).
                    */}
                    <InfoRow columns={2}>
                      <AccessSettingItem
                        setting="collect_email"
                        scope="product"
                        own={product.collect_email ?? null}
                        company={triggerSettings?.collect_email ?? null}
                        onEdit={() => setEditingAccessSetting('collect_email')}
                      />
                      <AccessSettingItem
                        setting="collect_location"
                        scope="product"
                        own={product.collect_location ?? null}
                        company={triggerSettings?.collect_location ?? null}
                        onEdit={() => setEditingAccessSetting('collect_location')}
                      />
                    </InfoRow>
                  </InfoBlock>

                  </Stack>
              </Grid>
          </Grid>
          <EditActivationTriggerModal
            open={openActivationModal}
            onClose={() => setOpenActivationModal(false)}
            scope="product"
            current={product.activation_trigger ?? null}
            inheritedTitle={activationTriggerTitle(inheritedTrigger)}
            inheritedTrigger={inheritedTrigger}
            loading={isSettingActivationTrigger}
            onSave={next =>
              setActivationTrigger({ id: product.id, activation_trigger: next })
            }
          />

          {/*
            VRG-591 — rendered only while a flag is being edited, so `current` and
            `inheritedValue` are always read for THAT flag. A permanently-mounted
            modal keyed off a nullable setting would need both values computed
            defensively for a null key.
          */}
          {editingAccessSetting && (
            <EditAccessSettingModal
              open
              onClose={() => setEditingAccessSetting(null)}
              setting={editingAccessSetting}
              scope="product"
              current={product[editingAccessSetting] ?? null}
              // What this product falls back to with no override of its own. The
              // shared resolver, not a local `?? true`, so the modal's inherit row
              // cannot disagree with the row behind it.
              inheritedValue={
                resolveAccessSetting(
                  { company: triggerSettings?.[editingAccessSetting] ?? null },
                  true,
                ).value
              }
              loading={isSettingAccessSetting}
              onSave={next =>
                setAccessSetting({
                  id: product.id,
                  [editingAccessSetting]: next,
                } as Parameters<typeof setAccessSetting>[0])
              }
            />
          )}

          {/*
            VRG-836 — the product convert's gate. Its copy is deliberately NOT the
            certificate's: a product conversion bills nothing, and one sentence for both
            would either overstate this or understate the billable one.
          */}
          <ConvertDemoRowModal
            kind="product"
            open={openConvertDemoModal}
            onClose={() => setOpenConvertDemoModal(false)}
            onConfirm={handleConfirmConvertDemoProduct}
            loading={isConvertingDemoProduct}
            name={product?.title}
            certificatesCount={product?.certificates_count}
            error={convertDemoProductError}
          />
          <DeleteProductModal
            product={{ ...product, certificates_count: product.certificates_count ?? 0 } as ProductWithCertificatesCount}
            open={openDeleteModal}
            onClose={() => setOpenDeleteModal(false)}
            refetch={refetch}
            onDeleted={() => navigate(ROUTES.PRODUCTS)}
          />
          <SetDefaultTagModal
            product={product} 
            open={openSetDefaultTagModal}
            onClose={() => setOpenSetDefaultTagModal(false)}
            refetch={refetch}
          />
           <CreateCertificatesModal
              product={selectedProduct || product}
              open={openCreateCertificatesModal}
              onClose={handleCloseCreateCertificatesModal}
              refetch={refetch}
            />
        </>
   </>)
}
